import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions} from '@angular/material/form-field';
import {stagger20ms} from "../../../../@config/animations/stagger.animation";
import {scaleFadeIn400ms} from "../../../../@config/animations/scale-fade-in.animation";
import {fadeInUp400ms} from "../../../../@config/animations/fade-in-up.animation";
import {TableColumn} from "../../../../@config/interfaces/table-column.interface";
import {SelectionModel} from "@angular/cdk/collections";
import {UntypedFormControl} from "@angular/forms";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {ExcelService} from "../../../services/excel.service";
import {defaultAvatar, isMobile, serverDate, today} from "../../variables/variables";
import {secureStorage} from "../../functions/secure-storage";
import {PRIMARY_OUTLET, Router, UrlTree} from "@angular/router";
import {CopyMessage, downloadFromBlobResponse, GetLanguage} from "../../functions/shared-functions";
import {debounceTime} from "rxjs";
import {LoaderService} from "../../../services/loader.service";
import moment from "moment";
import {CoreService} from "../../../services/core.service";
import {TranslateService} from "@ngx-translate/core";
import {ToastrService} from "ngx-toastr";

@UntilDestroy()
@Component({
  selector: 'app-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss'],
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'standard'
      } as MatFormFieldDefaultOptions
    }
  ],
  animations: [
    stagger20ms,
    fadeInUp400ms,
    scaleFadeIn400ms
  ]
})
export class DataTableComponent<T> implements OnInit, OnChanges, AfterViewInit {
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
  @ViewChild(MatSort, {static: true}) sort: MatSort;
  ///////////////////////////////////////////////////////////////////////////////////////
  @Input() data: T[];
  @Input() total;
  @Input() next_page;
  @Input() columns: TableColumn<T>[];
  @Input() pageSize = 20;
  @Input() pageSizeOptions = [10, 20, 50];
  @Input() pluralTableLabel: string;
  @Input() singleTableLabel: string;
  @Input() hasAdd: boolean;
  @Input() hasAddToPassedUsers: boolean;
  @Input() hasRemoveFromPassedUsers: boolean;
  @Input() hasGenerateCert: boolean;
  @Input() hasViewCert: boolean;
  @Input() isFiltering: boolean;
  @Input() publishExam: boolean;
  @Input() hasAddFromBank: boolean;
  @Input() enableRowClick: boolean;
  @Input() hasViewImages: boolean;
  @Input() hasAddQuestions: boolean;
  @Input() hasManagePerformance: boolean;
  @Input() hasQuestions: boolean;
  @Input() hasInstructions: boolean;
  @Input() hasDelete: boolean;
  @Input() hasView: boolean;
  @Input() hasTransfer: boolean;
  @Input() hasTransferToSupportType: boolean;
  @Input() hasReply: boolean;
  @Input() hasContent: boolean;
  @Input() hasAnswers: boolean;
  @Input() hasPromoCodes: boolean;
  @Input() hasPublish: boolean;
  @Input() hasModify: boolean;
  @Input() hasServerSearch: boolean = true;
  @Input() hasRecord: boolean;
  @Input() hasPlay: boolean;
  @Input() statues: any;
  @Input() clearSelection: any;
  ///////////////////////////////////////////////////////////////////////////////////////
  @Output() export = new EventEmitter<any>();
  @Output() getData = new EventEmitter<any>();
  @Output() openRow = new EventEmitter<any['id']>();
  @Output() deleteData = new EventEmitter<any>();
  @Output() addToPassedUsersEmitter = new EventEmitter<any>();
  @Output() removeFromPassedUsersEmitter = new EventEmitter<any>();
  @Output() viewCertEmitter = new EventEmitter<any>();
  @Output() generateCertEmitter = new EventEmitter<any>();
  @Output() editSelectedWeights = new EventEmitter<any>();
  @Output() downloadImportStudents = new EventEmitter<any>();
  @Output() importStudentsData = new EventEmitter<any>();
  @Output() downloadImportTeachers = new EventEmitter<any>();
  @Output() importTeachersData = new EventEmitter<any>();
  @Output() downloadImportQuestions = new EventEmitter<any>();
  @Output() importQuestionsData = new EventEmitter<any>();
  @Output() editSelectedQuestions = new EventEmitter<any>();
  @Output() addToExam = new EventEmitter<any>();
  @Output() zeroBalance = new EventEmitter<any>();
  @Output() createRow = new EventEmitter<any>();
  @Output() publishExamEvent = new EventEmitter<any>();
  @Output() viewRow = new EventEmitter<any>();
  @Output() complaint_reply = new EventEmitter<any>();
  @Output() complaint_transfer = new EventEmitter<any>();
  @Output() transferToSupportType = new EventEmitter<any>();
  @Output() playSession = new EventEmitter<any>();
  @Output() changed_status = new EventEmitter<any>();
  @Output() fireGetServerData = new EventEmitter<any>();
  @Output() addQuestionsToExam = new EventEmitter<any>();
  @Output() studentClicked = new EventEmitter<any>();
  @Output() ticketClicked = new EventEmitter<any>();
  @Output() sessionClicked = new EventEmitter<any>();
  @Output() teacherClicked = new EventEmitter<any>();
  @Output() serviceClicked = new EventEmitter<any>();
  @Output() openUserRecord = new EventEmitter<any>();
  @Output() navigateToContent = new EventEmitter<any>();
  @Output() navigateToAnswers = new EventEmitter<any>();
  @Output() navigateToPromoCodes = new EventEmitter<any>();
  @Output() publishCourse = new EventEmitter<any>();
  @Output() navigateToQuestions = new EventEmitter<any>();
  @Output() navigateToPerformance = new EventEmitter<any>();
  @Output() navigateToInstructions = new EventEmitter<any>();
  @Output() resendNoty = new EventEmitter<any>();
  @Output() certIssuance = new EventEmitter<any>();
  @Output() certShare = new EventEmitter<any>();
  @Output() certView = new EventEmitter<any>();
  @Output() viewScreenshotsImages = new EventEmitter<any>();
  ///////////////////////////////////////////////////////////////////////////////////////
  selection = new SelectionModel<any>(true, []);
  searchCtrl = new UntypedFormControl();
  pageIndex = 0;
  // visibleColumns: Array<keyof T | string>;
  dataSource = new MatTableDataSource<T>();
  selectedRow;
  isMobile = isMobile;
  defaultAvatar = defaultAvatar
  language = GetLanguage();
  filtering = false;
  statuses = [
    {
      name: 'Opened',
      value: 1,
      color: '#026c17'
    },
    {
      name: 'Pending',
      value: 2,
      color: '#efc48f',
    },
    {
      name: 'Closed',
      value: 3,
      color: '#b90707'
    },
  ];
  url = window.location.href;
  loading = false;
  queryParams;
  urlTree: UrlTree;

  constructor(private excelService: ExcelService,
              private router: Router,
              private coreService: CoreService,
              private translateService: TranslateService,
              private toastr: ToastrService,
              private loaderService: LoaderService,
  ) {
  }

  ngOnInit() {
    this.searchCtrl.valueChanges.pipe(
      untilDestroyed(this), debounceTime(1000)
    ).subscribe(value => this.onFilterChange(value));
    this.subscribeToLoading();
  }

  subscribeToLoading() {
    this.loaderService.loaderState.pipe(untilDestroyed(this)).subscribe((loader) => setTimeout(() => {
      this.loading = !!loader?.show;
    }));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.columns) {
      this.handleColumnsSort();
    }

    if (this.clearSelection) {
      this.selection.clear();
    }
    if (changes.data) {
      this.selection.clear();
      this.dataSource.filter = null;
      this.dataSource.data = this.data;
    }
  }

  handleColumnsSort() {
    this.columns.map((column: any) => {
      column.hasSort = column?.label?.length && column.hasSort !== false;
    });
  }

  onFilterChange(value: string) {
    this.filtering = !!value;
    // if (!value) {
    //   // console.log('value', value)
    //   return;
    // }
    value = value.trim();
    value = value.toLowerCase();
    // this.dataSource.filter = value;
    if (this.hasServerSearch) {
      this.fireGetServerData.emit(value);
      this.queryParams = `search=${value}`
    } else {
      this.dataSource.filter = value;
    }
    // if (this.dataSource.filteredData.length === 0) {
    // }
  }

  changeSort(event) {
    if (event.active === 'name' ||
      event.active === 'title' ||
      event.active === 'body' ||
      event.active === 'question' ||
      event.active === 'answer' ||
      event.active === 'text' ||
      event.active === 'content' ||
      event.active === 'description') {
      event.active = GetLanguage() === 'ar' ? `${event.active}_ar` : `${event.active}_en`;
    }
    if (event.active === 'teacher' ||
      event.active === 'student' ||
      event.active === 'exam' ||
      event.active === 'language' ||
      event.active === 'country'
    ) {
      event.active = `${event.active}_id`;
    }
    if (event.active === 'note' || event.active === 'timeslots' || event.active === 'preferred_days' || event.active === 'teacher_requests') {
      return;
    }
    const sort = `&sort_by=${event.active}&direction=${event.direction || 'asc'}`;
    this.fireGetServerData.emit(sort);
  }

  get visibleColumns(): Array<keyof T | string> {
    return this.columns.filter(column => column.visible !== false).map(column => column.property);
  }

  get filterColumns(): TableColumn<T>[] {
    return this.columns.filter(column => column.label?.length)
  }

  ngAfterViewInit() {
    // this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  headerClicked(event) {
    event.preventDefault()
  }

  toggleColumnVisibility(column, event) {
    event.stopPropagation();
    event.stopImmediatePropagation();
    column.visible = !column.visible;
  }

  delete(selected) {
    this.deleteData.emit(selected);
  }


  editWeights(selected) {
    this.editSelectedWeights.emit(selected)
  }

  downloadImportStudentsFile() {
    this.downloadImportStudents.emit()
  }

  importStudents() {
    this.importStudentsData.emit()
  }

  downloadImportTeachersFile() {
    this.downloadImportTeachers.emit()
  }

  importTeachers() {
    this.importTeachersData.emit()
  }

  downloadImportQuestionsFile() {
    this.downloadImportQuestions.emit()
  }

  importQuestions() {
    this.importQuestionsData.emit()
  }

  editQuestions(selected) {
    this.editSelectedQuestions.emit(selected)
  }

  addInExamFromBank(selected) {
    this.addToExam.emit(selected)
  }

  create() {
    this.createRow.emit();
  }

  publishExamQuestions() {
    this.publishExamEvent.emit();
  }


  changePage(event) {
    // if (this.next_page !== -1) {
    this.getData.emit({...event, pageIndex: event.pageIndex + 1});
    this.selection.clear();
    // }
  }


  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return (numSelected === numRows || numSelected === this.total);
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    if (this.isAllSelected()) {
      this.selection.clear();
      this.selection.selected.length = 0;
    } else {
      this.dataSource.data.forEach(row => this.selection.select(row));
      this.selection.selected.length = this.total;
    }
  }


  exportExcel(type) {
    var body = {};
    if (type === 'select') {
      body['ids'] = this.selection.selected?.map(x => x.id);
    }
    if (this.pluralTableLabel === 'Users') {
      this.coreService.postRequest(`admin/export/users${this.queryParams ? '?' + this.queryParams : ''}`, body).pipe(untilDestroyed(this)).subscribe(res => {
        console.log('res', res)
        downloadFromBlobResponse(res, `application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`, this.translateService.instant('Users'));
      }, error => {
        this.toastr.error('Export error occurred')
      });
    }
    if (this.pluralTableLabel === 'Challenges') {
      this.coreService.postRequest(`admin/export/challenges-data${this.queryParams ? '?' + this.queryParams : ''}`, body).pipe(untilDestroyed(this)).subscribe(res => {
        console.log('res', res)
        downloadFromBlobResponse(res, `application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`, this.translateService.instant('Challenges'));
      }, error => {
        this.toastr.error('Export error occurred')
      });
    }
    if (this.pluralTableLabel === 'Doors') {
      this.coreService.postRequest(`admin/export/doors${this.queryParams ? '?' + this.queryParams : ''}`, body).pipe(untilDestroyed(this)).subscribe(res => {
        console.log('res', res)
        downloadFromBlobResponse(res, `application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`, this.translateService.instant('Doors'));
      }, error => {
        this.toastr.error('Export error occurred')
      });
    }
    if (this.pluralTableLabel === 'Reference numbers') {
      this.coreService.postRequest(`admin/export/references${this.queryParams ? '?' + this.queryParams : ''}`, body).pipe(untilDestroyed(this)).subscribe(res => {
        console.log('res', res)
        downloadFromBlobResponse(res, `application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`, this.translateService.instant('Reference numbers'));
      }, error => {
        this.toastr.error('Export error occurred')
      });
    }
    if (this.pluralTableLabel === 'Performance management') {
      this.urlTree = this.router.parseUrl(this.router.url);
      const challenge_id = Number(this.urlTree.root.children[PRIMARY_OUTLET].segments[1].path);
      this.coreService.postRequest(`admin/export/challenges?challenge_id=${challenge_id}${this.queryParams ? '&' + this.queryParams : ''}`, body).pipe(untilDestroyed(this)).subscribe(res => {
        console.log('res', res)
        downloadFromBlobResponse(res, `application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`, this.translateService.instant('Performance management'));
      }, error => {
        this.toastr.error('Export error occurred')
      });
    }
    // if (this.pluralTableLabel === 'Students' ||
    //   this.pluralTableLabel === 'Teachers' ||
    //   this.pluralTableLabel === 'Sessions Requests' ||
    //   this.pluralTableLabel === 'Sessions') {
    //   this.export.emit(type === 'select' ? this.selection.selected : null);
    // } else {
    //   const data = type === 'select' ? this.selection.selected : this.dataSource.data;
    //   const headers = this.filterColumns.map(column => column.label);
    //   const values = this.filterColumns.map(column => column.property);
    //   console.log(data)
    //   console.log(values)
    //   console.log(headers)
    //   this.excelService.generateExcel(data, values, headers);
    // }
  }

  generateCert() {
    if (this.selection.selected?.length) {
      console.log(this.selection.selected)
      this.generateCertEmitter.emit(this.selection.selected)
    } else {
      this.toastr.error(this.translateService.instant('You have to select users first !'))
    }
  }

  sendNotification(all?) {
    this.selection.selected.forEach(item => {
      item.type = this.pluralTableLabel;
    })
    if (this.isAllSelected()) {
      secureStorage.setItem('selected', this.dataSource.data);
    } else {
      secureStorage.setItem('selected', this.selection.selected);
    }
    if (all && !this.isFiltering) {
      this.router.navigate(['/notifications/create-notification'], {
        queryParams: {
          all: all,
          notification_type: this.pluralTableLabel?.toLowerCase()
        }
      })
    } else if (all && this.isFiltering) {
      secureStorage.setItem('selected', this.dataSource.data);
      this.router.navigate(['/notifications/create-notification'], {
        queryParams: {
          notification_type: this.pluralTableLabel?.toLowerCase()
        }
      })
    } else {
      this.router.navigate(['/notifications/create-notification'], {
        queryParams: {
          notification_type: this.pluralTableLabel?.toLowerCase()
        }
      })
    }
  }

  view(row) {
    this.viewRow.emit(row);
    this.complaint_reply.emit(row);
  }

  copyMessage(val: string) {
    CopyMessage(val, this.toastr, this.translateService);
  }

  transferToSupportTicket(row) {
    this.complaint_transfer.emit(row);
  }

  transferToAnotherSupportType(row) {
    this.transferToSupportType.emit(row);
  }

  playSessionFun(row) {
    this.playSession.emit(row);
  }

  resendNotification(row) {
    this.resendNoty.emit(row);
  }

  certificateIssuance(rows) {
    this.certIssuance.emit(rows);
  }

  certificateShare(rows) {
    this.certShare.emit(rows);
  }

  certificateView(row) {
    this.certView.emit(row);
  }

  viewScreenshots(row) {
    this.viewScreenshotsImages.emit(row);
  }

  reply(row) {
    this.complaint_reply.emit(row);
  }

  changeStatus(event) {
    const e = {
      row: this.selectedRow,
      value: event
    }
    if (this.selectedRow.status.value !== event?.value) {
      this.changed_status.emit(e);
    }
  }

  gotoContent(row) {
    this.navigateToContent.emit(row);
  }

  gotoAnswers(row) {
    this.navigateToAnswers.emit(row);
  }

  gotoPromoCodes(row) {
    this.navigateToPromoCodes.emit(row);
  }

  publish(row) {
    // console.log('row', row)
    this.publishCourse.emit(row);
  }

  rowClicked(row) {
    this.studentClicked.emit(row);
    this.ticketClicked.emit(row);
    this.sessionClicked.emit(row);
    this.teacherClicked.emit(row);
    this.serviceClicked.emit(row);
  }

  openFile(row) {
    this.openUserRecord.emit(row);
  }

  disableEdit() {
    if (this.pluralTableLabel.includes('Content')) {
      return new Date(this.selectedRow?.started_at).getTime() <= today.getTime();
    }
    return false;
  }

  disableDelete() {
    if (this.pluralTableLabel.includes('Content')) {
      return new Date(this.selectedRow?.started_at).getTime() <= today.getTime();
    }
    if (this.pluralTableLabel.includes('Courses categories')) {
      const ids = [1, 2, 3]
      return ids.includes(this.selectedRow?.id);
    }
    return false;
  }

  gotoQuestions(row) {
    this.navigateToQuestions.emit(row);
  }

  gotoPerformance(row) {
    this.navigateToPerformance.emit(row);
  }

  gotoInstructions(row) {
    this.navigateToInstructions.emit(row);
  }

  scrollTable(e) {
    // // console.log('sss', e.target.scrollLeft)
    // const paginator = document.getElementById('paginator');
    // const header = document.getElementById('table-bar');
    // if (e.target.scrollLeft <= 0) {
    //   header.style.position = 'fixed';
    //   header.style.left = '0';
    // } else {
    //   header.style.position = 'relative';
    //   header.style.right = 'auto';
    // }
  }
}
